import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import endpoints from "../../../api/endpoints";
import api from "../../../api/axios";
import { extractApiErrorMessage } from "../../../utils/apiError";

// Initial state
interface EmployeeBalance {
  currentRemainingBalance: number;
  currentTotalActualAmount: number;
  currentEligibleAmount: number;
  previousRemainingBalance: number;
  previousTotalActualAmount: number;
  previousEligibleAmount: number;
}

interface TuitionApplyState {
  employeeDetails: any;
  dropdownOptions: Record<string, any[]>;
  checkboxOptions: any[];
  loading: boolean;
  error: string | null;
  savePatchError?: string | null;
  employeeInfoPatchError: string | null; // Added separate error state for employee info patch
  updateManagerDetailsError?: string | null;
  isEligible: boolean; // Added eligibility state
  eligibilityReason: string | null;
  altNuidResult: any;
  altNuidError: string | null;
  employeeBalance: EmployeeBalance | null; // Updated type for employee balance
  employeeBalanceError: string | null; // Added separate error state for employee balance
  applicationId: string | null;
}

const initialState: TuitionApplyState = {
  employeeDetails: null,
  dropdownOptions: {},
  checkboxOptions: [],
  loading: false,
  error: null,
  savePatchError: null,
  employeeInfoPatchError: null, // Initialize employee info patch error
  updateManagerDetailsError: null,
  isEligible: false, // Initialize eligibility state
  eligibilityReason: null,
  altNuidResult: null,
  altNuidError: null,
  employeeBalance: null, // Initialize employee balance
  employeeBalanceError: null, // Initialize employee balance error
  applicationId: null,
};

export const fetchTuitionEmployeeDetails = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("tuition/fetchTuitionEmployeeDetails", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(
      endpoints.tuition.tuitionApply.getEmployeeDetails,
    );
    if (response.data?.success === false) {
      return rejectWithValue(
        extractApiErrorMessage({ response }, "Failed to fetch employee details"),
      );
    }
    const mappedData = {
      ...response.data,
      empShift: response.data.shiftStdHours ?? "",
    };
    return mappedData;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(error, "Failed to fetch employee details"),
    );
  }
});

export const fetchDropdownOptions = createAsyncThunk<
  any,
  { fieldName: string; applType: string },
  { rejectValue: string }
>(
  "tuition/fetchDropdownOptions",
  async ({ fieldName, applType }, { rejectWithValue }) => {
    try {
      const url = `${endpoints.tuition.tuitionApply.getDropdownOptions}?fieldName=${fieldName}&applType=${applType}`;
      const response = await api.get(url);
      return { fieldName, options: response.data }; // Return fieldName with options
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "An error occurred"),
      );
    }
  },
);

export const fetchCheckboxOptions = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("tuition/fetchCheckboxOptions", async (_, { rejectWithValue }) => {
  try {
    const url = `${endpoints.tuition.tuitionApply.getCheckboxOptions}?applType=AT001`;
    const response = await api.get(url);
    return response.data;
  } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "An error occurred"),
      );
  }
});

export const fetchClassEligibility = createAsyncThunk<
  { isEligible: boolean; reason: string | null },
  string, // Changed to accept courseStartDate as a parameter
  { rejectValue: string }
>(
  "tuition/fetchClassEligibility",
  async (courseStartDate: string, { rejectWithValue }) => {
    try {
      const url = `${endpoints.tuition.tuitionApply.getClassInfoEligibility}?applType=AT001&courseStartDate=${courseStartDate}`;
      const response = await api.get(url);
      return { isEligible: response.data.eligible, reason: null };
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          "Failed to check class eligibility , please try again later",
        ),
      );
    }
  },
);
export const saveTuitionEmployeeDetails = createAsyncThunk<
  any,
  {
    altPhone?: number;
    prefPhone?: string;
    homeEmail?: string;
    prefEmail?: string;
  },
  { rejectValue: string }
>(
  "tuition/saveTuitionEmployeeDetails",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        endpoints.tuition.tuitionApply.saveEmployeeDetails,
        payload,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to save employee details"),
      );
    }
  },
);

export const searchAltNuid = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>(
  "tuition/searchAltNuid",
  async (nuid: string, { rejectWithValue, signal }) => {
    try {
      if (!nuid) throw new Error("NUID is required");

      const url = endpoints.tuition.tuitionApply.searchAltNuid;
      const options: any = {
        signal,
        headers: {
          "X-emp-nuid": nuid,
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      };

      const response = await api.get(url, options);

      // Handle 304 Not Modified by returning empty object
      if (response.status === 304 || !response.data) {
        return {};
      }

      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request was cancelled");
      }

      return rejectWithValue(
        extractApiErrorMessage(
          error,
          "Failed to search alternate NUID due to a server error, please try again after sometime",
        ),
      );
    }
  },
);

export const updateManagerDetails = createAsyncThunk<
  any,
  { managerPhone?: string; altManagerPhone?: string; altManagerNuid?: string },
  { rejectValue: string }
>("tuition/updateManagerDetails", async (payload, { rejectWithValue }) => {
  try {
    const response = await api.patch(
      endpoints.tuition.tuitionApply.updatingManagerDetails,
      payload,
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(error, "Failed to update manager details"),
    );
  }
});
export const fetchMilesData = createAsyncThunk<
  any,
  { courseStartDate: string; totalMiles: number },
  { rejectValue: string }
>(
  "tuition/fetchMilesData",
  async ({ courseStartDate, totalMiles }, { rejectWithValue }) => {
    try {
      const url = `${endpoints.tuition.tuitionApply.getMileRateInfo}?courseStartDate=${courseStartDate}&totalMiles=${totalMiles}`;
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      if (error?.message) {
        let errorMessage = error.message;
        console.log("error", errorMessage);
      }
      return rejectWithValue(
        extractApiErrorMessage(error, "Failed to fetch mile rate information"),
      );
    }
  },
);

export const submitApplicationForm = createAsyncThunk(
  "tuition/submitApplicationForm",
  async (payload: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await api.post(
        endpoints.tuition.tuitionApply.submitApplicationForm,
        payload,
      );
      return response.data;
    } catch (error: any) {
      console.error("Error submitting application form:", error);
      return rejectWithValue(
        extractApiErrorMessage(
          error,
          "Failed to submit application form, please try again later",
        ),
      );
    }
  },
);
export const fetchEmployeeBalance = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("tuition/fetchEmployeeBalance", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(
      `${endpoints.tuition.employeeOverview.getEmployeeBalance}?applType=AT001`,
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      extractApiErrorMessage(error, "Failed to fetch employee balance"),
    );
  }
});
// Slice
const tuitionApplySlice = createSlice({
  name: "tuitionApply",
  initialState,
  reducers: {
    setEligibility(
      state,
      action: PayloadAction<{ isEligible: boolean; reason: string | null }>,
    ) {
      state.isEligible = action.payload.isEligible;
      state.eligibilityReason = action.payload.reason;
    },
    resetEligibility(state) {
      state.isEligible = false;
      state.eligibilityReason = null;
    },
    clearAltNuidError(state) {
      state.altNuidError = null;
    },
    clearAltNuidResult(state) {
      state.altNuidResult = null;
    },
    clearSavePatchError(state) {
      state.savePatchError = null;
    },
    clearUpdateManagerDetailsError(state) {
      state.updateManagerDetailsError = null;
    },
    clearAllErrors(state) {
      state.error = null;
      state.altNuidError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTuitionEmployeeDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTuitionEmployeeDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employeeDetails = action.payload;
        },
      )
      .addCase(
        fetchTuitionEmployeeDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null; // Don't set error for fetch failures - keep form visible
          // Don't set error for fetch failures - they shouldn't block form rendering
        },
      )
      .addCase(fetchDropdownOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDropdownOptions.fulfilled, (state, action) => {
        state.loading = false;

        state.dropdownOptions = { ...action.payload.options };
      })

      .addCase(
        fetchDropdownOptions.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : null;
        },
      )
      .addCase(fetchCheckboxOptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCheckboxOptions.fulfilled, (state, action) => {
        state.loading = false;
        state.checkboxOptions = action.payload; // Store fetched checkbox options
      })
      .addCase(
        fetchCheckboxOptions.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error =
            typeof action.payload === "string" ? action.payload : null;
        },
      )
      // .addCase(fetchClassEligibility.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      .addCase(
        fetchClassEligibility.fulfilled,
        (
          state,
          action: PayloadAction<{ isEligible: boolean; reason: string | null }>,
        ) => {
          state.loading = false;
          state.isEligible = action.payload.isEligible;
          state.eligibilityReason = action.payload.reason;
        },
      )
      .addCase(
        fetchClassEligibility.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null;
        },
      )
      .addCase(saveTuitionEmployeeDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.savePatchError = null;
        state.employeeInfoPatchError = null;
      })
      .addCase(
        saveTuitionEmployeeDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employeeDetails = {
            ...state.employeeDetails,
            ...action.payload,
          };
          state.savePatchError = null;
          state.employeeInfoPatchError = null;
        },
      )
      .addCase(
        saveTuitionEmployeeDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          // Don't set state.error for patch errors - keep form visible with banner
          state.savePatchError = action.payload || null;
          state.employeeInfoPatchError = action.payload || null;
        },
      )
      .addCase(searchAltNuid.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.altNuidError = null;
      })
      .addCase(searchAltNuid.fulfilled, (state, action) => {
        state.loading = false;
        state.altNuidResult = action.payload;
        state.altNuidError = null;
      })
      .addCase(
        searchAltNuid.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.altNuidError = action.payload || "Failed to search NUID";
          state.altNuidResult = null;
        },
      )
      .addCase(updateManagerDetails.pending, (state) => {
        state.loading = true;
        state.updateManagerDetailsError = null;
      })
      .addCase(
        updateManagerDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.updateManagerDetailsError = null;
          state.employeeDetails = {
            ...state.employeeDetails,
            ...action.payload,
          };
        },
      )
      .addCase(
        updateManagerDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.updateManagerDetailsError =
            action.payload || "Failed to update manager details";
        },
      )
      .addCase(fetchMilesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMilesData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(
        fetchMilesData.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || null;
        },
      )
      .addCase(submitApplicationForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        submitApplicationForm.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.applicationId = action.payload.applicationId; // Store applicationId in the state
        },
      )
      .addCase(submitApplicationForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchEmployeeBalance.pending, (state) => {
        state.loading = true;
        state.employeeBalanceError = null;
      })
      .addCase(
        fetchEmployeeBalance.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.employeeBalance = action.payload; // Store fetched employee balance
          state.employeeBalanceError = null;
        },
      )
      .addCase(
        fetchEmployeeBalance.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.employeeBalanceError = action.payload || null;
        },
      );
  },
});

export const {
  setEligibility,
  resetEligibility,
  clearAltNuidError,
  clearAltNuidResult,
  clearSavePatchError,
  clearUpdateManagerDetailsError,
  clearAllErrors,
} = tuitionApplySlice.actions;

export default tuitionApplySlice.reducer;
